import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Card, Collapse, Form, Container, Button } from 'react-bootstrap';
import { SingleSelectAsync, SelectOption } from './SIngleSelectAsync';
import { MultiSelectAsync } from './MultiSelectAsync';
import FeatureNav from './FeatureNav';
import FooterSection from './FooterSection';
import HeaderComponent from './HeaderComponent';
import BreadCrumbs from './BreadCrumbs';
import Loader from './Loader';
import { Tabulator, PageModule, DownloadModule, ExportModule, SortModule } from "tabulator-tables";
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';
import "tabulator-tables/dist/css/tabulator.min.css";
import * as d3 from "d3";

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule]);

interface CuratedJob {
	class: string;
	class_id: number;
	fiscal_year: number;
}

interface AllEntity {
  name: string;
  id: number;
}

interface MasterData {
  fiscal_year: number;
  entity_name: string;
  amount: string;
  statistic: string;
}

interface JobTitleBase {
  entity_id: number;
  entity_name: string;
  fiscal_year: number;
  wages: number;
  benefits: number;
  total: number;
  title: string;
  employee_name: string;
  title_beg_id: string;
  govt_lvl: string;
}

interface DataPoint {
  date: number;
  amount: number;
  year: number;
}

interface ChartSeries {
  key: string;
  color: string;
  values: DataPoint[];
}

const EmployeesJobTitleWageTrends = () => {
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<SelectOption | null>(null);
  const [curatedOptions, setCuratedOptions] = useState<SelectOption[]>([]);
  const [curatedJobs, setCuratedJobs] = useState<CuratedJob[]>([]);
  const [masterData, setMasterData] = useState<MasterData[]>([]);
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(null); 
  const [selectedEntityNames, setSelectedEntityNames] = useState<string [] | null>(null);
  const [selectedFiscalYears, setSelectedFiscalYears] = useState<number [] | null>(null);
  const [filteredData, setFilteredData] = useState<JobTitleBase[] | null>(null);
  const [allEntities, setAllEntities] = useState<string[]>([]);
  const [allFiscalYears, setAllFiscalYears] = useState<string[] | null>(null);
  const [customSearchText, setCustomSearchText] = useState<string | null>(null);
  const [processedD3Data, setProcessedD3Data] = useState<ChartSeries[] | null>(null);
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [selctedCompensationType, setSelctedCompensationType] = useState<"Total Compensation" | "Wages Only" | "Benefits Only" | null>("Total Compensation");
  const [searchOptionType, setSearchOptionType] = useState<"Curated Titles" | "Custom Search" | null>("Curated Titles");
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
  const [loading, setLoading] = useState<boolean>(false);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
    const formattedEntityName = selectedEntityName?.toLowerCase()?.replaceAll(" ", "_") || "Employee";
    if (!tabulatorRef.current) return;
    tabulatorRef.current.download(type, `${formattedEntityName}_job_title_wage_trends.${type}`);
  };

  const loadJobOptions = async (inputValue: string): Promise<SelectOption[]> => {
    if (inputValue) {
      return curatedOptions.filter(option =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    }
     
    return curatedOptions
  };

  // Updated handleSubmit
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    let jobToFetch = selectedJob?.value;

    if (searchOptionType === "Custom Search" && customSearchText) {
      const customJob = {
        value: customSearchText,
        label: customSearchText,
      };
      setSelectedJob(customJob);
      jobToFetch = customSearchText;
    }

    if (jobToFetch) {
      fetchJobTitleWageTrendData(jobToFetch);
    } else {
      setLoading(false);
    }
  };

  const clearFilters = (event: React.FormEvent) => {
    event.preventDefault();
    setSelectedEntityNames(null);
  }

  const buildApiUrl = (apiEndpointUrl: string, searchParameter: string) => {
    return `${apiEndpointUrl}${searchParameter}`;
  }

  const getAllEntitiesSearchString = `?function=getAllEntities&parameter=%7B%7D`;
  const compensationParam = selctedCompensationType === "Wages Only" ? "wages" : selctedCompensationType === "Benefits Only" ? "benefits" : "total";

  const payload = { 
    title: selectedJob?.label || selectedJob?.value || selectedJob, 
    compensation_type: compensationParam 
  };
  

  // Updated the to select all entities
  const fetchJobTitleWageTrendData = async (overrideJobTitle?: string) => {
    setLoading(true);

    try {
      const compensationParam = selctedCompensationType === "Wages Only" ? "wages" : selctedCompensationType === "Benefits Only" ? "benefits" : "total";
      
      const activeTitle = overrideJobTitle || selectedJob?.label || selectedJob?.value || selectedJob;

      if (!activeTitle) {
        setLoading(false);
        return;
      }

      const payload = { 
        title: activeTitle, 
        compensation_type: compensationParam 
      };
      
      const jobTitleWageTrendsEncodedParam = encodeURIComponent(JSON.stringify(payload));
      const jobTitleWageTrendsSearchString = `/?function=jobTitleWageTrendFreeSearchLong&parameter=${jobTitleWageTrendsEncodedParam}`;

      const res = await fetch(buildApiUrl(apiEndpoint, jobTitleWageTrendsSearchString));
      const jobTitleWageTrendsData = res.ok ? await res.json() : [];

      const getFormOptions = (data: any[], key: string) => {
        const unique = [...new Set(data.map((item: any) => item[key]?.toString()))]
        .filter(Boolean)
        .sort((a, b) => {
          const aNum = Number(a);
          const bNum = Number(b);
          return (!isNaN(aNum) && !isNaN(bNum)) ? aNum - bNum : a.localeCompare(b);
        });

        return unique.map(val => ({ value: val, label: val }));
      };

      const getFiscalYearFormOptions = (data: any[], key: string) => {
        const unique = [...new Set(data.map(item => item[key]?.toString()))]
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        return unique.map(val => ({
          value: val,
          label: val
        }));
      }
      
      const fiscalYears = getFormOptions(jobTitleWageTrendsData, "fiscal_year");
      const fiscalYearString = fiscalYears.map(fiscalYear => String(fiscalYear.value));
      const entities = getFormOptions(jobTitleWageTrendsData, "entity_name");
      const entityString = entities.map(entity => entity.value);

      setAllEntities(entityString);
      setAllFiscalYears(fiscalYearString);
      setMasterData(jobTitleWageTrendsData);
      setFilteredData(jobTitleWageTrendsData);

    } catch (error: any) {
      if (error.name !== 'AbortError') console.log("Fetch failed fetchJobTitleWageTrends");
    } finally {
      setLoading(false);
    }
  };

  // Get all curated jobs
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      setLoading(true);
      try {
        const entities = await getAllEntities();

        await fetchAllCuratedJobs();
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('Initialization error FetchData');
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    const getAllEntities = async (): Promise<AllEntity[]> => {
      try {
        const response = await fetch(buildApiUrl(apiEndpoint, getAllEntitiesSearchString), { signal });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        
        return data
          .filter((entity: any) => entity.entity_id && entity.entity_name)
          .map((entity: any) => ({
            name: String(entity.entity_name),
            id: entity.entity_id,
          }))
      } catch (error: any) {
        if (error.name !== 'AbortError') console.error('fetch error getAllEntities');
        return [];
      }
    };

    const fetchAllCuratedJobs = async () => {
      try {
        const curatedJobTitleSearchString = "/?function=getCuratedHighestTitles&parameter=%7B%7D";

        const res = await fetch(buildApiUrl(apiEndpoint, curatedJobTitleSearchString));
        const curatedData = res.ok ? await res.json() : [];

        if (!signal.aborted && Array.isArray(curatedData)) {
          const uniqueClasses = [...new Set(curatedData.map((item: CuratedJob) => item.class))] as string[];
          const formattedOptions: SelectOption[] = uniqueClasses.map(className => ({
            value: className,
            label: className
          }));
          setCuratedOptions(formattedOptions);
          setCuratedJobs(curatedData);
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') console.error('fetch error fetchAllCuratedJobs');
      }
    };

    fetchData();

    return () => controller.abort();
  }, []);

  // Initialize Tabulator Table add data and implement filters
  useEffect(() => {
    if (!tableRef.current || !masterData || masterData.length === 0) return;

    setLoading(true);

    // Pivot to accommodate tabulator 
    const pivotedMap = masterData.reduce((acc: any, item: any) => {
      const rowId = `${item.fiscal_year}-${item.entity_name}`;

      // Initialize new row if not seen yet
      if (!acc[rowId]) {
        acc[rowId] = { 
          fiscal_year: item.fiscal_year, 
          entity_name: item.entity_name, 
        };
      }

      acc[rowId][item.statistic] = item.amount;

      return acc;
    }, {});

    // Flatten array for tabulator
    let tableData: JobTitleBase[] = Object.values(pivotedMap);

    // Add Filters
    if (selectedEntityNames?.length) {
      tableData = tableData.filter(item => selectedEntityNames.includes(item.entity_name));
    }

    if (selectedFiscalYears?.length) {
      tableData = tableData.filter(item => selectedFiscalYears.includes(item.fiscal_year));
    }

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Destroy existing table instance before making a new one
    if (tabulatorRef.current) {
      tabulatorRef.current.destroy();
    }

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: tableData,
      minHeight: "450px",
      maxHeight: "100%",
      layout: isMobile ? "fitDataFill" : "fitColumns", 
      pagination: "local",
      paginationSize: 10,
      placeholder: "No Data Available",
      headerSort: true,
      initialSort: [
        {
          column: "fiscal_year",
          dir: "desc",
        }
      ],
      columns: [
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-calendar'></i> Year";
            return el;
          },
          title: "Fiscal Year",
          field: "fiscal_year",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-landmark'></i> Entity";
            return el;
          },
          title: "Entity",
          field: "entity_name", 
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-arrow-down-wide-short'></i> Min";
            return el;
          },
          title: "Min",
          field: "min",
          sorter: "number",
          formatter: "money",
          formatterParams: { symbol: "$" }
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-chart-line'></i> 25th Percentile";
            return el;
          },
          title: "25th Percentile",
          field: "percentile25",
          sorter: "number",
          formatter: "money",
          formatterParams: { symbol: "$" }
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-arrows-left-right-to-line'></i> Median";
            return el;
          },
          title: "Median",
          field: "median",
          sorter: "number",
          formatter: "money",
          formatterParams: { symbol: "$" }
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-angle-up'></i> 75th Percentile";
            return el;
          },
          title: "75th Percentile",
          field: "percentile75",
          sorter: "number",
          formatter: "money",
          formatterParams: { symbol: "$" }
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-step-forward fa-rotate-270'></i> Max";
            return el;
          },
          title: "Max",
          field: "max",
          sorter: "number",
          formatter: "money",
          formatterParams: { symbol: "$" }
        },
      ],
    });

    setLoading(false);

    // Added to help with table visibility issues
    if (tabulatorRef.current) {
      setTimeout(() => {
        if (tabulatorRef.current) {
          try {
            tabulatorRef.current.redraw(true);
          } catch (error) {
          }
        }
      }, 0);
    }
  }, [masterData, selectedEntityNames, selectedFiscalYears]);

  // Reset filters on new vendor search
  useEffect(() => {
    setSelectedFiscalYears(null);
    setSelectedEntityNames(null);
  }, [masterData]);

  // Process Data for D3 Chart
  useEffect(() => {
    if (!masterData.length) return;

    const stats = ["max", "percentile75", "median", "percentile25", "min"];
    const colors = ["#C3D2E3", "#66748F", "#3D4766", "#66748F", "#C3D2E3"];

    const processedData: ChartSeries[] = stats.map((stat, i) => {
      const filtered = masterData.filter(d => {
        const matchesStat = d.statistic === stat; 
        const hasAmount = d.amount != null && !isNaN(Number(d.amount));
        
        const matchesEntity = !selectedEntityNames || selectedEntityNames.length === 0 || selectedEntityNames.includes(d.entity_name);

        const matchesYear = !selectedFiscalYears || selectedFiscalYears.length === 0 || selectedFiscalYears.map(String).includes(d.fiscal_year.toString());

        return matchesStat && hasAmount && matchesEntity && matchesYear;
      });

      const uniqueByYear = Array.from(
        d3.rollup(filtered, v => v[0], d => d.fiscal_year).values()
      );

      return {
        key: stat,
        color: colors[i],
        values: uniqueByYear
          .map(d => ({
            date: d.fiscal_year,
            amount: Number(d.amount),
            year: d.fiscal_year
          }))
          .sort((a, b) => a.year - b.year)
      };
    });

    setProcessedD3Data(processedData);
  }, [masterData, selectedEntityNames, selectedFiscalYears]);

  // Draw D3 Chart
  useEffect(() => {
    if (!processedD3Data || processedD3Data.length === 0 || !chartRef.current || !containerRef.current) return;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const containerWidth = containerRef.current?.clientWidth ?? 1200;
    const ratio = containerWidth > 1000 ? 0.35 : 0.6;
    const totalHeight = containerWidth * ratio;
    const margin = { 
      top: 30, 
      right: isMobile ? 30 : 150,
      bottom: isMobile ? 80: 60, 
      left: isMobile? 50 : 80,
    };
    const width = containerWidth - margin.left - margin.right;
    const height = totalHeight - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    const chartGroup = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Tooltips
    const tooltip = d3.select(containerRef.current)
      .append("div")
      .attr("class", "chart-tooltip-unique")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background", "white")
      .style("border", "1px solid gray")
      .style("padding", "5px")
      .style("border-radius", "4px")
      .style("pointer-events", "none")
      .style("font-size", "12px")
      .style("z-index", "9999");

    // Scales
    const allValues = processedD3Data.flatMap(d => d.values);
    const x = d3.scaleLinear()
      .domain(d3.extent(allValues, d => d.date) as [number, number]) 
      .range([0, width]);
    const maxY = d3.max(allValues, d => d.amount) || 0;
    const y = d3.scaleLinear()
      .domain([0, maxY * 1.1])
      .range([height, 0]);

    const lineGenerator = d3.line<any>()
      .x(d => x(d.date))
      .y(d => y(d.amount))
      .curve(d3.curveCardinal);

    // Draw Lines
    chartGroup.selectAll(".line-path")
      .data(processedD3Data)
      .join("path")
      .attr("class", "line-path")
      .attr("fill", "none")
      .attr("stroke", d => d.color)
      .attr("stroke-width", d => d.key === "median" ? 6 : 2) 
      .attr("d", d => lineGenerator(d.values));

    // Mobile Optimized legend - moves to bottom for mobile screens
    const legend = chartGroup.append("g")
      .attr("transform", isMobile 
        ? `translate(0, ${height + 50})`
        : `translate(${width + 20}, 0)`
      );

    const formatLabel = (key: any, isMobile: any) => {
      if (key === "percentile25") return isMobile ? "25th" : "25th Percentile";
      if (key === "percentile75") return isMobile ? "75th" : "75th Percentile";
      if (key === "median") return isMobile ? "Med" : "Median";

      return key.charAt(0).toUpperCase() + key.slice(1);
    }

    processedD3Data.forEach((series, i) => {
      const itemsPerRow = 3;
      const xPosition = isMobile ? (i % itemsPerRow) * 85 : 0;
      const yPosition = isMobile ? Math.floor(i / itemsPerRow) * 20 : (i * 25);

      const row = legend.append("g")
        .attr("transform", `translate(${xPosition}, ${yPosition})`);

      row.append("circle")
        .attr("r", isMobile ? 4 : 6)
        .attr("fill", series.color);

      row.append("text")
        .attr("x", isMobile ? 10 : 15)
        .attr("y", 5)
        .style("font-size", isMobile ? "9px" : "12px")
        .style("font-weight", "500")
        .text(formatLabel(series.key, isMobile));
    });

    // Axes
    chartGroup.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3.axisBottom(x).tickFormat((d) => {
          const yearString = d.toString();
          return isMobile ? `'${yearString.slice(-2)}` : yearString;
        })
      ) 

    chartGroup.append("g")
      .call(d3.axisLeft(y).tickFormat(d3.format("$,d")));

    // Entity Name Legend
    if (selectedEntityNames && selectedEntityNames.length > 0) {
      const entityLegend = chartGroup.append("g")
        .attr("class", "entity-legend")
        .attr("transform", `translate(${width / 2}, ${height + 35})`);  

      entityLegend.append("text")
        .attr("x", 0) 
        .attr("y", 0)
        .style("font-size", "12px")
        .style("font-weight", "bold")
        .style("text-anchor", "middle")
        .style("fill", "#333")
        .text(selectedEntityNames.join(", "));
    }

    // Draw Circles (Points)
    const allPoints = processedD3Data.flatMap(series =>
      series.values.map(v => ({ ...v, color: series.color, key: series.key }))
    );

    chartGroup.selectAll("circle.data-point")
      .data(allPoints)
      .join("circle")
      .attr("class", "data-point")
      .attr("cx", d => x(d.date))
      .attr("cy", d => y(d.amount))
      .attr("r", d => d.key === "median" ? 8 : 4)
      .attr("fill", d => d.color)
      .style("cursor", "pointer")
      .on("mouseover", (event, d) => {
        tooltip
          .style("visibility", "visible")
          .style("color", "#333333")
          .html(`
            <div style="font-weight: bold; margin-bottom: 4px;">
              ${formatLabel(d.key, isMobile)} 
            </div>
            <div>Year: ${d.year}</div>
            <div>Amount: ${d3.format("$,d")(d.amount)}</div>
          `);
      })
      .on("mousemove", (event) => {
        const [posX, posY] = d3.pointer(event, containerRef.current);
        tooltip
          .style("top", `${posY - 45}px`)
          .style("left", `${posX + 15}px`);
      })
      .on("mouseout", () => {
        tooltip.style("visibility", "hidden");
      });

    return () => {
      // Cleanup tooltips on unmount or re-render
      d3.selectAll(".chart-tooltip-unique").remove();
    };
  }, [processedD3Data, selectedEntityNames]);

  // Add options for chart type radio form
	const chartTypeOptions = ["Total Compensation", "Wages Only", "Benefits Only"];

  // Add options for search option section
  const searchOptions = ["Curated Titles", "Custom Search"];

  // Add memoized options for Entity MultiSelect Form
  const entityNameOptions = useMemo(() =>
    (allEntities || []).map(entity_name => ({ value: entity_name, label: entity_name})),
  [allEntities]);

  const fiscalYearOptions = useMemo(() => 
    (allFiscalYears || []).map(year => ({ value: year.toString(), label: year.toString() })), 
  [allFiscalYears]);

  return (
    <>
      <HeaderComponent/>
      <FeatureNav/>
      <BreadCrumbs/>
      <Container fluid as="main">
        <Row className="mb-4 px-4">
          <Col xs={12} className="px-0">
            <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body style={{ justifyItems: "center", textAlign: "center" }}>
                <div className="tu_tabHeader">
                  <div className="tu-tabHeader__titleRow">
                    <h1 className="tu-tabHeader__title">
                    Job Title - Wage Trends</h1>
                      <button
                      type="button"
                      className="tu-tabHeader__infoBtn"
                      onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                      aria-label="Job Title Wage Trend Info"
                    >
                      <i className="fas fa-info"/>
                      </button>
                  </div>
                </div>
                <Collapse in={showHeaderInfo}>
                  <div>
                    <p className="lead mt-2 text-center">
                      The Office of the State Auditor has manually grouped comparable jobs titles under a common name to allow utahns to compare compensation packages across entities.
                    </p>
                    <p className="lead mt-2 text-center">
                      This page allows you to see a job's wage trends across fiscal years. Select from the curated job title drop down or perform a free search. Next, click the compensation type and click "Submit".
                    </p>
                  </div>
                </Collapse>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Loader */}
        {loading ? <Loader /> : <></>}

        {/* Search Option Form */}
        <Form onSubmit={handleSubmit}>
          <Row className="px-4 mb-3">
            <Col xs={12} className="px-0">
              <Card>
                <Card.Header className="bg-secondary text-white" style={{ border: "none" }}>
                  <h3 className="text-white">Search</h3>
                </Card.Header>
                <Card.Body className="px-3 py-2">
                  
                  {/* Search Option Section */}
                  <Row>
                    <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-2">
                      <h5>
                        <i className="fas fa-magnifying-glass" />
                        {" "}Search Option
                      </h5>
                      <div className="mb-2">
                        {searchOptions.map((option) => (
                          <Form.Check
                            key={option}
                            inline
                            type="radio"
                            label={option}
                            name="searchOptions"
                            id={`radio-${option.replace(/\s+/g, '-').toLowerCase()}`}
                            checked={searchOptionType === option}
                            onChange={() => setSearchOptionType(option as "Curated Titles" | "Custom Search")}
                          />
                        ))}
                      </div>
                    </Col>
                  </Row>

                  {/* Search Job Title Section */}
                  <Row>
                  {searchOptionType == "Curated Titles" ? (
                    <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-2">
                      <h5>
                        <i className="fas fa-briefcase" />
                        {" "}Search Job Title
                      </h5>
                      <SingleSelectAsync
                        value={selectedJob}
                        key={curatedOptions.length}
                        onChange={(newValue) => setSelectedJob(newValue)}
                        loadOptions={loadJobOptions}
                        type="title"
                        defaultOptions={curatedOptions}
                      />
                    </Col>
                  ) : (
                    <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-2">
                      <h5>
                        <i className="fas fa-briefcase" />
                          {" "}Enter Job Title
                      </h5>

                      <Form.Control 
                        type="text" 
                        placeholder="Enter Job Title Here" 
                        value={customSearchText ?? ""} 
                        onChange={(event) => setCustomSearchText(event.target.value)}
                        className="mt-2"
                      />
                    </Col>
                  )}
                  </Row>

                  {/* Compensation Type Section */}
                  <Row>
                    <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-2">
                      <h5>
                        <i className="fas fa-solid fa-money-bill-1" />
                        {" "}Compensation Type
                      </h5>
                      <div className="mb-2">
                        {chartTypeOptions.map((option) => (
                          <Form.Check
                            key={option}
                            inline
                            type="radio"
                            label={option}
                            name="chartTypeGroup"
                            id={`radio-${option.replace(/\s+/g, '-').toLowerCase()}`}
                            checked={selctedCompensationType === option}
                            onChange={() => setSelctedCompensationType(option as "Total Compensation" | "Wages Only" | "Benefits Only")}
                          />
                        ))}
                      </div>
                    </Col>
                  </Row>

                  {/* Submit Button Section */}
                  <Row>
                    <Col xs={12} className="my-2">
                      <button
                        type="submit"
                        className="btn btn-secondary"
                        aria-label="Submit"
                      >
                        <i className="fas fa-search"></i>{" "} Search
                      </button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>

        {/* Filter Section */}
        <Row className="px-4 mb-3">
          <Col xs={12} className="px-0">
            <Card>
              <Card.Header className="bg-secondary text-white" style={{ border: "none"}}>
                <h3 className="text-white">Filter</h3>
              </Card.Header>
              <Card.Body className="p-2">
                <Row>
                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <h5>
                      <i className="fas fa-landmark"/>
                      {" "}Filter Entity
                    </h5>
                    <MultiSelectAsync
                      options={entityNameOptions}
                      defaultOptions={entityNameOptions}
                      value={selectedEntityNames ? selectedEntityNames.map(name => ({ value: name, label: name})) : []}
                        isSearchable={true}
                      onChange={(selected) => {
                        const entityNames = selected ? selected.map(s => s.value) : [];
                        setSelectedEntityNames(entityNames);
                      }}
                      loadOptions={async (inputValue) => {
                        if (!inputValue) return entityNameOptions;
                        return entityNameOptions.filter(o =>
                          o.label.toLowerCase().includes(inputValue.toLowerCase())
                        );
                      }}
                    />
                  </Col>

                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <h5>
                      <i className="fas fa-calendar"/>
                      {" "}Filter Year
                    </h5>
                    <MultiSelectAsync
                      options={fiscalYearOptions}
                      defaultOptions={fiscalYearOptions}
                      value={
                        selectedFiscalYears?.map(year => ({
                          value: year.toString(),
                          label: year.toString(),
                        })) || []
                      }
                      isSearchable={true}
                      onChange={(selected) => {
                        const values = selected ? selected.map(s => Number(s.value)) : [];
                        setSelectedFiscalYears(values);
                      }}
                      loadOptions={async (inputValue) => {
                        if (!inputValue) return fiscalYearOptions;
                        return fiscalYearOptions.filter(o =>
                          o.label.toString().toLowerCase().includes(inputValue.toLowerCase())
                        );
                      }}
                      closeMenuOnSelect={false}
                    />
                  </Col>

                  {/* Clear Filters */}
                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      aria-label="Clear Filters"
                      onClick={clearFilters}
                      style={{ marginTop: window.innerWidth > 1025 ? "33px" : "0" }}
                    >
                      <i className="fas fa-rotate-left"></i>{" "} Clear Filters
                    </button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Compensation Trend Line Chart */}
        <Row className="px-4 mb-3">
          <Col xs={12} className="px-0">
            <Card>
              <Card.Header className="bg-secondary text-white" style={{ border: "none"}}>
                <h3 className="text-white">Compensation Trend Line Chart</h3>
              </Card.Header>
              <Card.Body>
                <div
                  className="btn btn-secondary"
                  id="btn-chart-download"
                  onClick={() => {
                    if (!chartRef.current) return;
                    const currentEntityName = selectedEntityName || "Entity";
                    const bounds = chartRef.current.getBoundingClientRect();
                    const width = bounds.width;
                    const height = bounds.height;
                    const scale = 2;
                    const clonedSvg = chartRef.current.cloneNode(true) as unknown as SVGSVGElement;
                      
                    clonedSvg.setAttribute("width", width.toString());
                    clonedSvg.setAttribute("height", height.toString());

                    const serializer = new XMLSerializer();
                    const svgString = serializer.serializeToString(clonedSvg);
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                    const url = URL.createObjectURL(svgBlob);

                    const img = new Image();
                    img.onload = () => {
                      canvas.width = width * scale;
                      canvas.height = height * scale;

                      if (context) {
                        context.fillStyle = "white";
                        context.fillRect(0, 0, canvas.width, canvas.height);

                        context.drawImage(img, 0, 0, canvas.width, canvas.height);
                      }
                        
                      URL.revokeObjectURL(url);
                      const pngUrl = canvas.toDataURL('image/png');
                      const a = document.createElement('a');
                      const formattedName = currentEntityName.toLowerCase().replaceAll(" ", "_");
                      a.download = `${formattedName}_job_title_wage_trends.png`;
                      a.href = pngUrl;
                      a.click();
                      };
                      img.src = url;
                    }}
                    style={{ margin: 0, position: "relative" }}
                  >
                    Download
                  </div>
                  <div ref={containerRef} style={{ position: "relative" }}>
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                      <Loader />
                  </div>
                  ) : processedD3Data && processedD3Data.length > 0 ? (
                    <svg
                      ref={chartRef}
                      width="100%"
                      height="auto"
                      viewBox={`0 0 ${containerRef.current?.clientWidth ?? 1200} ${
                        (containerRef.current?.clientWidth ?? 1200) * 
                        ((containerRef.current?.clientWidth ?? 1200) > 1000 ? 0.35 : 0.6)
                      }`}
                      preserveAspectRatio="xMidYMid meet"
                      style={{ display: "block", 
                              maxHeight: "600px",
                              margin: "0 auto"
                      }}
                    />
                  ) : (
                    <Card.Text className="text-center py-5">No data available for the selected filters. Try adjusting your search or selecting a new search term.</Card.Text>
                    )
                  } 
                  </div>
                  <div style={{ marginTop: "12px", fontWeight: "bold", fontSize: "16px", textAlign: "center" }}>
                  {selectedEntityName}
                  </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Data Section */}
        <Row className="px-4 mb-3">
          <Col xs={12} className="px-0">
            <Card className="mt-4 shadow-sm">
              <Card.Header className="bg-secondary text-white">
                <h3 className="text-white">Data</h3>
              </Card.Header>
              <Card.Body>
                <div className="mb-2 d-flex gap-2">
                  <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                </div>
                <div style={{ width: "100%", position: "relative"}}>
                  {/* Loading */}
                  {loading && (
                    <div className="text-center py-5">
                      <div className="text-primary" role="status"></div>
                      {/* Loader */}
                      {loading ? <Card.Text><Loader /></Card.Text> : <></>}
                    </div>
                  )}

                  {/* No Data Available */}
                  {!loading && (!filteredData || filteredData.length === 0) && (
                    <div 
                      className="d-flex justify-content-center align-items-center" 
                      style={{ 
                        position: "absolute",
                        top: 0, 
                        left: 0, 
                        width: "100%", 
                        height: "300px", 
                        zIndex: 10 
                      }}
                    >
                      <Card.Text className="text-center m-0 px-4">
                        No data available for the selected filters. Try adjusting your search or selecting a new search term.
                      </Card.Text>
                    </div>
                  )}

                  <div 
                    ref={tableRef} 
                    className="table-light"
                    style={{ 
                      minHeight: 400,
                      visibility: (!loading && filteredData && filteredData.length > 0) ? 'visible' : 'hidden',
                      height: 'auto',
                      overflow: 'hidden'
                    }} 
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <FooterSection/>
    </>
  )
}

export default EmployeesJobTitleWageTrends;